/**
 * fillDisplay.js - Utility für die Anzeige der Automaten-Füllung
 * 
 * Verwendet current_fill am Automaten als robuste Datenquelle.
 * Zeigt Produktnamen pro Schacht an, oder "KEIN INHALT" wenn leer.
 */

/**
 * Gibt den Anzeige-Text für einen bestimmten Schacht zurück.
 * 
 * @param {Object} automat - Automaten-Objekt mit current_fill oder content_preview
 * @param {number} slotIndex - 1-basierter Schacht-Index
 * @returns {Object} { text: string, isEmpty: boolean }
 */
export const getSlotFillText = (automat, slotIndex) => {
  // Versuche zuerst current_fill (robuste Quelle)
  const currentFill = automat?.current_fill;
  
  if (currentFill) {
    const slotData = currentFill[String(slotIndex)];
    if (slotData && slotData.product_name) {
      return {
        text: slotData.product_name,
        isEmpty: false
      };
    }
  }
  
  // Fallback: content_preview (abgeleitete Quelle vom Backend)
  const contentPreview = automat?.content_preview;
  
  if (contentPreview && Array.isArray(contentPreview)) {
    const slotItem = contentPreview.find(item => item.slot_number === slotIndex);
    if (slotItem && slotItem.product_name) {
      return {
        text: slotItem.product_name,
        isEmpty: false
      };
    }
  }
  
  // Kein Inhalt gefunden
  return {
    text: 'KEIN INHALT',
    isEmpty: true
  };
};

/**
 * Gibt alle Schacht-Füllungen für einen Automaten zurück.
 * 
 * @param {Object} automat - Automaten-Objekt mit current_fill oder content_preview
 * @param {number} maxSlots - Maximale Anzahl Schächte (default: 2)
 * @returns {Array} Array von { slotNumber, text, isEmpty }
 */
export const getAllSlotsFill = (automat, maxSlots = 2) => {
  const slots = [];
  
  // Bestimme die Anzahl der Schächte aus current_fill oder content_preview
  const currentFill = automat?.current_fill;
  const contentPreview = automat?.content_preview;
  
  let slotCount = maxSlots;
  
  if (currentFill && Object.keys(currentFill).length > 0) {
    slotCount = Math.max(...Object.keys(currentFill).map(k => parseInt(k)), maxSlots);
  } else if (contentPreview && contentPreview.length > 0) {
    slotCount = Math.max(...contentPreview.map(item => item.slot_number || 1), maxSlots);
  }
  
  for (let i = 1; i <= slotCount; i++) {
    const fillInfo = getSlotFillText(automat, i);
    slots.push({
      slotNumber: i,
      text: fillInfo.text,
      isEmpty: fillInfo.isEmpty
    });
  }
  
  return slots;
};

/**
 * Prüft, ob ein Automat überhaupt Inhalt hat.
 * 
 * @param {Object} automat - Automaten-Objekt
 * @returns {boolean} true wenn mindestens ein Schacht gefüllt ist
 */
export const hasContent = (automat) => {
  const currentFill = automat?.current_fill;
  
  if (currentFill && Object.keys(currentFill).length > 0) {
    return Object.values(currentFill).some(slot => slot && slot.product_name);
  }
  
  const contentPreview = automat?.content_preview;
  
  if (contentPreview && contentPreview.length > 0) {
    return contentPreview.some(item => item.product_name);
  }
  
  return false;
};

export default {
  getSlotFillText,
  getAllSlotsFill,
  hasContent
};
